import React from 'react';
import {Box, Typography} from '@mui/material';
import {useHistory} from 'react-router-dom';
import {useAuth} from '../../../context/auth';
import {getUserDetailsService, updateUserDetailsService} from '../../../services/auth.service';
import {isClientOnboardingUser, mergeOnboardingRefreshIntoAuthUser} from '../../DQPro/DQProV2/constants/onboardingSteps.config';
import ScopeSuppressedBanner from '../../DashboardCommon/ScopeSuppressedBanner.comp';

/**
 * Shared page shell for every School Dashboard section.
 * Renders the page header (title + Back to Workspace button), optional tabs, and {children}.
 */
const SchoolDashboardShell = ({tabs, children, needsAssessmentResults = false}) => {
    const history = useHistory();
    const {authUser, setAuthUser} = useAuth();

    // Onboarding: viewing the school dashboard is the completion signal for the
    // level-22 client's Step 3 ("Track") — parallels the owner's ImpactDashboard.
    // Gated on p0 + level-22. Fire-and-forget.
    React.useEffect(() => {
        if (!isClientOnboardingUser(authUser)) return;
        updateUserDetailsService({complete_onboarding_step: 3})
            .then(() => getUserDetailsService())
            .then(res => {
                const refreshedUser = res?.data?.user || res?.data;
                // Merge ONLY onboarding progress — a full replace drops
                // login-time session fields (see mergeOnboardingRefreshIntoAuthUser).
                const merged = mergeOnboardingRefreshIntoAuthUser(authUser, refreshedUser);
                if (merged !== authUser) setAuthUser(merged);
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box>
            {/* Page header */}
            <Box sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                mb: 2,
                pb: 2.5,
                borderBottom: '1px solid #E5E7EB',
                flexWrap: 'wrap',
                gap: 2,
            }}>
                <Box>
                    <Typography sx={{
                        fontSize: {xs: '20px', md: '26px'},
                        fontWeight: 800,
                        color: '#052F44',
                        lineHeight: 1.2,
                    }}>
                        School Dashboard
                    </Typography>
                    <Typography sx={{fontSize: '14px', color: '#6A7282', mt: 0.5}}>
                        Child online safety readiness, cyber risk and support environment for students
                    </Typography>
                </Box>
            </Box>

            {/* Scores hidden when the school's own population is under the n floor */}
            <ScopeSuppressedBanner/>

            {/* Persistent Tabs Navigation - rendered above content */}
            {tabs}

            {/* Section content rendered unconditionally so child pages/tabs run their own lifecycle */}
            {children}
        </Box>
    );
};

export default SchoolDashboardShell;
